import { Box, IconButton, Paper, Typography } from '@mui/material'
import { ArrowDownload20Regular } from '@/components/Icon'
import { extensionFromName } from '@/components/AiAskBar/attachmentMetaExtension.js'
import Tooltip from '@/components/Tooltip'
import Pressable from '@/theme/Pressable'
import {
  getArtifactPayload,
  resolveArtifactForPreview,
} from '@/features/documentPreview/artifactPayloadStore.js'
import { downloadArtifactExport } from '@/features/documentPreview/downloadArtifactExport.js'
import { getArtifactExportText } from '@/features/documentPreview/getArtifactExportText.js'
import { useDocumentPreviewOptional } from '@/features/documentPreview/useDocumentPreview'
import FileArtifactTypeIcon from './FileArtifactTypeIcon.jsx'
import styles from './fileArtifactBlock.module.scss'

export default function FileArtifactBlockView({
  fileName = 'Untitled Document.docx',
  description = '',
  artifactKind = 'doc',
  artifactId = '',
}) {
  const preview = useDocumentPreviewOptional()
  const isZip = extensionFromName(fileName) === 'zip'
  const isSelected =
    !isZip && Boolean(artifactId) && preview?.activeArtifact?.artifactId === artifactId

  const resolveArtifact = () => {
    if (!artifactId) return null
    return (
      getArtifactPayload(artifactId) ??
      resolveArtifactForPreview({
        artifactId,
        kind: artifactKind,
        fileName,
        description,
      })
    )
  }

  const resolvedArtifact = resolveArtifact()
  const canDownload = Boolean(resolvedArtifact && getArtifactExportText(resolvedArtifact).trim())

  const handleDownload = (event) => {
    event?.stopPropagation()
    if (!resolvedArtifact) return
    downloadArtifactExport(resolvedArtifact)
  }

  const handleOpen = () => {
    if (isZip) {
      handleDownload()
      return
    }
    if (!artifactId || !preview) return
    preview.openPreview({
      artifactId,
      kind: artifactKind,
      fileName,
      description,
    })
  }

  return (
    <Paper elevation={0} className={styles.root}>
      <Box className={styles.chipRow}>
        <Pressable
          className={`${styles.chip} ${isSelected ? styles.chipSelected : ''}`}
          aria-label={isZip ? `Download ${fileName}` : `Open ${fileName}`}
          onClick={handleOpen}
          disabled={!artifactId || (!isZip && !preview)}
        >
          <Box className={styles.fileIconPreview} aria-hidden>
            <FileArtifactTypeIcon fileName={fileName} artifactKind={artifactKind} />
          </Box>
          <Box className={styles.fileMeta}>
            <Typography component="p" className={styles.fileName}>
              {fileName}
            </Typography>
            {description ? (
              <Typography component="p" className={styles.fileInfo}>
                {description}
              </Typography>
            ) : null}
          </Box>
        </Pressable>
        <Tooltip text="Download" placement="bottom">
          <span>
            <IconButton
              type="button"
              size="small"
              className={styles.downloadButton}
              onClick={handleDownload}
              disabled={!artifactId || !canDownload}
              aria-label={`Download ${fileName}`}
            >
              <ArrowDownload20Regular aria-hidden />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Paper>
  )
}
